/**
 * Blob storage resolution for asset URL builders (CAE-676).
 *
 * There is no default storage account. When `VITE_BLOB_STORAGE_ACCOUNT_NAME` is
 * unset or blank the account resolves to `undefined` and every builder
 * downstream returns no URL, so an instance with no blob storage renders
 * placeholder artwork instead of composing URLs against some other operator's
 * estate. The container keeps a default because it is a path segment inside
 * whichever account is configured, so it names no estate on its own.
 *
 * A local copy of packages/frontend-shared/src/blob-storage.ts, which the admin
 * does not vendor — its vendor:sync set is api-types, ui, shared-types,
 * sports-config, style-config and tenant-config.
 */

const CONTAINER_FALLBACK = 'picks-core';

/** The configured storage account, or `undefined` when none is configured. */
export function getBlobStorageAccount(): string | undefined {
  return import.meta.env?.VITE_BLOB_STORAGE_ACCOUNT_NAME?.trim() || undefined;
}

/** The configured container. Defaults to the platform's shared asset container. */
export function getBlobStorageContainer(): string {
  return import.meta.env?.VITE_BLOB_STORAGE_CONTAINER?.trim() || CONTAINER_FALLBACK;
}

/**
 * Base URL for public assets — `https://{account}.blob.core.windows.net/{container}`.
 * `undefined` when no account is configured; callers must render no image.
 */
export function getBlobAssetBaseUrl(): string | undefined {
  const account = getBlobStorageAccount();
  if (!account) return undefined;
  return `https://${account}.blob.core.windows.net/${getBlobStorageContainer()}`;
}
